import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { Theme } from "@mui/material";
import { CircularProgress, styled, Typography, useMediaQuery } from "@mui/material";
import {
  CodeInput,
  Header,
  MekiDocSplashScreen,
  NavigateBeforeButton,
  FullScreenLoader,
  FormBody,
  FormContentContainer,
  SecondaryActionButton,
  ColoredComponent,
  IconOptions,
} from "@components";
import { DoctorDocumentReadinessModal } from "@components/documentCreation";
import { AuthFeedbackModal } from "@components/Modals/AuthFeedbackModal";
import { useAuth, useDocumentSignatureContext } from "@contexts";
import {
  useRequestData,
  useVerificationCode,
  useReEnlistment,
  useRequestSigningStatus,
  useDoctorDocumentReadiness,
} from "@hooks";
import {
  checkIfBiometricAuthIsAvailable,
  getFromLocalStorage,
  onContactUsClick,
  maskPhone,
  maskEmail,
} from "@utils";
import { redirectToDocumentCreationScreen } from "@utils/redirect";
import type { Doctor, RequestType } from "@interfaces";
import { AuthFlow, AuthMethod, AuthStep, defaultPinState } from "@interfaces";
import { StyledSuccessIcon } from "@styles/styled";
import {
  MainContainer,
  SignStepTypography,
  CodeContainer,
  CodeDescriptionText,
  IncorrectCodeText,
  ButtonContainer,
  NewLoginContactUsParagraph,
  NewLoginContactUsText,
  StyledErrorIcon,
  ContentContainer,
  CodeWrapper,
  CodeTopContainer,
  CodeBottomContainer,
  MoreInfoLink,
} from "./SignRecipeScreeSecondFactor.styles";
import { authCodeErrorWsp } from "../../constants";
import {
  BlockedPinDocumentSignatureErrorModal,
  UnknownDocumentCreationErrorModal,
  UnknownDocumentSignatureErrorModal,
  ValidationRequiredDocumentSignatureErrorModal,
  CertificateRevokedDocumentSignatureErrorModal,
  WrongPinDocumentSignatureErrorModal,
} from "./DocumentManipulationErrorModals";
import { useSnackbar } from "notistack";
import { SecondFactorExplanationModal } from "./components/SecondFactorExplanationModal";

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "23px",
  color: theme.palette.info.contrastText,
}));

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

export const SecondFactorExplanationModalBoldText = styled("span")({
  fontWeight: "600",
});

const FeedbackIcon = (authStep: AuthStep) => {
  switch (authStep) {
    case AuthStep.SUCCESS:
      return <StyledSuccessIcon />;
    case AuthStep.ERROR:
      return <StyledErrorIcon />;
    default:
      return <CircularProgress size={24} color="primary" />;
  }
};

const maxRetriesError = "Máximo de intentos excedido, solicita un nuevo código";
const unknownError = "Error verificando código, intentalo nuevamente";

const dummyLocation = {
  doctor: { email: "", phone: "" },
  authMethod: AuthMethod.EMAIL,
  pin: "",
} as const;

interface SignRecipeScreenSecondFactorLocationState {
  doctor: Doctor;
  authMethod: AuthMethod;
}

export function SignRecipeScreenSecondFactor() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { requestId = "" } = useParams();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const location = useLocation();

  const {
    doctor,
    doctor: { email, phone },
    authMethod: defaultAuthMethod,
  }: SignRecipeScreenSecondFactorLocationState = location.state || dummyLocation;

  const requestType = getFromLocalStorage<RequestType>({
    key: "requestType",
  }) as RequestType;

  const { data: requestData } = useRequestData();
  const { isSigning } = useRequestSigningStatus(requestData?.request.status);
  const { code, setCode, verifyCode, resendCode } = useVerificationCode();

  const {
    authChallengeFeedback: { message: authFeedback, authStep },
    handleAuthChallengeFeedback,
  } = useAuth();

  // Use the document signature context
  const { signPrescription, pinState, setPinState, isLoading, loadingTitle } =
    useDocumentSignatureContext();

  const { ensureReadyToIssue, modalProps } = useDoctorDocumentReadiness({
    requestId,
  });

  useEffect(() => {
    ensureReadyToIssue(() => {});
  }, [ensureReadyToIssue]);

  const onReadinessModalClose = useCallback(() => {
    modalProps.onClose();
    navigate(redirectToDocumentCreationScreen(requestType, requestId), {
      replace: true,
    });
  }, [modalProps, navigate, requestType, requestId]);

  // Use re-enlistment for error handling
  const {
    reEnlistmentMutation: { mutate: reEnlistment, isPending: isUnenrolling },
  } = useReEnlistment(requestId);

  const [authMethod, setAuthMethod] = useState(defaultAuthMethod);
  const [started, setStarted] = useState(true);
  const [timer, setTimer] = useState(60000);
  const [codeError, setCodeError] = useState<string>("");
  const [openExplanationModal, setOpenExplanationModal] = useState(false);

  const startRef = useRef<Date>();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const onScreenSuccess = useCallback(async () => {
    if (pinState.pin) {
      signPrescription(pinState.pin, requestId);
    }
  }, [pinState.pin, signPrescription, requestId]);

  const handleReEnlistment = useCallback(() => {
    reEnlistment(doctor, {
      onError: () => {
        // Handle re-enlistment error if needed
        console.error("Failed to re-enlist");
      },
    });
  }, [doctor, reEnlistment]);

  const handleCodeChange = useCallback(
    async (value: string) => {
      setCode(value);
      if (!requestData || value.length < 6) {
        if (value.length > 0) setCodeError("");

        return;
      }

      try {
        handleAuthChallengeFeedback({
          authMethod,
          authStep: AuthStep.VALIDATING,
        });
        const success = await verifyCode(value);

        if (success) {
          await handleAuthChallengeFeedback({
            authMethod,
            authStep: AuthStep.SUCCESS,
          });

          const isBiometricAuthAvailable = await checkIfBiometricAuthIsAvailable();

          const showBiometricSecondFactor =
            doctor.showBiometricSecondFactor !== false && isBiometricAuthAvailable;

          if (showBiometricSecondFactor) {
            navigate("/app/registro-biometrico", {
              state: {
                redirectTo: {
                  url: `/app/solicitudes/${requestId}/receta/firma`,
                  state: {
                    secondAuthFactorValidated: true,
                  },
                },
              },
            });
          } else {
            onScreenSuccess();
          }

          return;
        }

        if (!codeError) {
          handleAuthChallengeFeedback({
            resetToInitialState: true,
          });
          setCode("");
          setCodeError("Código incorrecto");
        }
      } catch (error) {
        handleAuthChallengeFeedback({
          resetToInitialState: true,
        });
        const parsedError = String(error);

        if (parsedError.includes("NotAuthorizedException")) {
          setCodeError(maxRetriesError);
        } else {
          setCodeError(unknownError);
        }
      }
    },
    [
      setCode,
      requestData,
      handleAuthChallengeFeedback,
      authMethod,
      verifyCode,
      codeError,
      doctor,
      navigate,
      requestId,
      onScreenSuccess,
    ],
  );

  const onResendCodeClick = useCallback(async () => {
    setCode("");
    setCodeError("");
    await resendCode(email, authMethod, AuthFlow.PRESCRIPTION);
    setStarted(true);
    setTimer(60000);
  }, [setCode, resendCode, email, authMethod]);

  const handleAuthMethodChange = useCallback(
    async (prevAuthMethod: AuthMethod) => {
      const newAuthMethod =
        prevAuthMethod === AuthMethod.PHONE_NUMBER
          ? AuthMethod.EMAIL
          : AuthMethod.PHONE_NUMBER;

      setAuthMethod(newAuthMethod);
      await resendCode(email, newAuthMethod, AuthFlow.PRESCRIPTION);
      setTimer(60000);
    },
    [resendCode, email],
  );

  const disabledButton = useMemo(
    () => timer > 0 && codeError !== maxRetriesError,
    [timer, codeError],
  );

  const handleBack = useCallback(() => {
    if (isSigning) {
      navigate("/app/solicitudes");
      enqueueSnackbar("La receta ya está en proceso de firma", {
        variant: "info",
      });

      return;
    }

    navigate(redirectToDocumentCreationScreen(requestType, requestId));
  }, [navigate, requestType, requestId, isSigning, enqueueSnackbar]);

  const resetPinAndRetrySign = useCallback(() => {
    setPinState(defaultPinState);
    navigate(`/app/solicitudes/${requestId}/receta/firma`, {
      state: {
        secondAuthFactorValidated: true,
      },
    });
  }, [requestId, setPinState, navigate]);

  const resetPinAndGoHome = useCallback(() => {
    setPinState(defaultPinState);
    navigate("/app/solicitudes");
  }, [setPinState, navigate]);

  useEffect(() => {
    if (started) {
      startRef.current = new Date();
      intervalRef.current = setTimeout(() => {
        if (timer < 0 || !startRef.current) {
          setStarted(false);
          clearTimeout(intervalRef.current);
        } else {
          setTimer(timer - (+new Date() - +startRef.current));
        }
      }, 1000);
    }

    return () => {
      clearTimeout(intervalRef.current);
    };
  }, [started, timer, startRef]);

  useEffect(() => {
    const redirectIfNotEnoughData = () => {
      if (!doctor) {
        navigate("/app");
      }

      if (!pinState.pin) {
        navigate(redirectToDocumentCreationScreen(requestType, requestId));
      }
    };

    redirectIfNotEnoughData();
  }, [navigate, doctor, pinState.pin, requestId, requestType]);

  if (!doctor) {
    return <MekiDocSplashScreen />;
  }

  // Show loading when signature is in progress
  if (isLoading) {
    return (
      <>
        <Header
          title={"Confirmación de cuenta"}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title={loadingTitle} />
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        title={isDesktop ? "Confirmación de cuenta" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Title variant="h5" fontWeight={"medium"}>
            Confirmación de cuenta
          </Title>
          <MainContainer>
            <SignStepTypography>Paso 2 de 2</SignStepTypography>
            <ContentContainer>
              <CodeContainer>
                <CodeWrapper>
                  <CodeTopContainer>
                    <CodeDescriptionText>
                      Ingresa el código que te enviamos{" "}
                      {authMethod === AuthMethod.PHONE_NUMBER
                        ? `por SMS al ${maskPhone(phone)}`
                        : `a ${maskEmail(email)}`}
                    </CodeDescriptionText>

                    <CodeInput
                      code={code}
                      handleCodeChange={handleCodeChange}
                      numInputs={6}
                      isValid={!codeError}
                      disabled={code.length === 6 && !codeError}
                      type={"number"}
                    />

                    {codeError && <IncorrectCodeText>{codeError}</IncorrectCodeText>}
                  </CodeTopContainer>
                  <CodeBottomContainer>
                    <ButtonContainer>
                      <SecondaryActionButton
                        onClick={onResendCodeClick}
                        disabled={disabledButton}
                      >
                        Volver a enviar código{" "}
                        {disabledButton
                          ? `(${new Date(timer)
                              .toISOString()
                              .split("T")[1]
                              .split(".")[0]
                              .slice(4)})`
                          : ""}
                      </SecondaryActionButton>

                      {authMethod === defaultAuthMethod ? (
                        <SecondaryActionButton
                          onClick={() => handleAuthMethodChange(authMethod)}
                        >
                          {authMethod === AuthMethod.PHONE_NUMBER
                            ? "Enviar el código por email"
                            : "Enviar el código por SMS"}
                        </SecondaryActionButton>
                      ) : (
                        <NewLoginContactUsParagraph>
                          Si tienes problemas{" "}
                          <NewLoginContactUsText
                            onClick={() => onContactUsClick(authCodeErrorWsp)}
                          >
                            contáctanos
                          </NewLoginContactUsText>
                        </NewLoginContactUsParagraph>
                      )}
                    </ButtonContainer>
                  </CodeBottomContainer>
                </CodeWrapper>
              </CodeContainer>
              <ColoredComponent
                color="#E9F5FE"
                icon={IconOptions.STYLED_INFO}
                text={
                  <InfoText>
                    Requerimiento de seguridad del proveedor de Firma Electrónica
                    Avanzada.{" "}
                    <MoreInfoLink onClick={() => setOpenExplanationModal(true)}>
                      Más info
                    </MoreInfoLink>
                    .
                  </InfoText>
                }
              />
            </ContentContainer>

            {/* Creation & Signature Error Modals */}
            <UnknownDocumentCreationErrorModal requestId={requestId} />

            <WrongPinDocumentSignatureErrorModal
              onPrimaryAction={resetPinAndRetrySign}
              onSecondaryAction={resetPinAndGoHome}
            />

            <BlockedPinDocumentSignatureErrorModal
              // onPrimaryAction={resetPin}
              onReEnlistment={handleReEnlistment}
              isUnenrolling={isUnenrolling}
              onSecondaryAction={resetPinAndGoHome}
            />

            <UnknownDocumentSignatureErrorModal
              onPrimaryAction={() => signPrescription(pinState.pin, requestId)}
              onSecondaryAction={resetPinAndGoHome}
            />

            <ValidationRequiredDocumentSignatureErrorModal
              onSecondaryAction={resetPinAndGoHome}
            />

            <CertificateRevokedDocumentSignatureErrorModal
              onReEnlistment={handleReEnlistment}
              isUnenrolling={isUnenrolling}
              onSecondaryAction={resetPinAndGoHome}
            />

            <DoctorDocumentReadinessModal
              {...modalProps}
              onClose={onReadinessModalClose}
            />

            {authFeedback && (
              <AuthFeedbackModal
                isOpen={true}
                authFeedback={authFeedback}
                authStep={authStep}
                feedbackIcon={FeedbackIcon}
              />
            )}

            <SecondFactorExplanationModal
              isOpen={openExplanationModal}
              onClose={() => setOpenExplanationModal(false)}
            />
          </MainContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
